import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CloseButton, Modal } from 'react-bootstrap';
import { Loader, Icon, PageTitle } from '@pwc/pwc-deals-ui';
import { getProject } from 'features/project/projectSlice';
import { isArray } from 'lodash';
import SupportTicketLink from '../SupportTicketLink';
import TableauErrorMessage from './TableauErrorMessage/TableauErrorMessage';
import { SELECT_REPORT_TYPE } from './SelectReportDetail';
import styles from './SelectReportList.module.scss';

export const SelectReportList = ({
  show = true,
  onHide = () => {},
  fileSelected = () => {},
  title,
  workspaceUrl,
  emptyListMessage,
  getReports,
  workspaceId,
  reportIcon,
  reportType,
}) => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const [reports, setReports] = useState([]);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState();
  const project = useSelector(getProject);
  useEffect(() => {
    setIsLoading(true);
    getReports(projectId, workspaceId).then((data) => {
      if (data) {
        setReports(data);
        setError(false);
      } else {
        setReports([]);
        setError(true);
      }

      setIsLoading(false);
    });
  }, [getReports, projectId, workspaceId]);

  const isEmpty = () => reports.length === 0;

  const getReportListTemplate = () => {
    return (
      <ul>
        {isArray(reports) &&
          reports.length &&
          reports.map((report, index) => (
            <li key={`${report.name + index}`}>
              <button
                type="button"
                onClick={() => fileSelected(report)}
                className="ui-button button-link button-theme"
              >
                {reportIcon}
                <div className="button-text">{report.name}</div>
              </button>
            </li>
          ))}
      </ul>
    );
  };

  const getMessageTemplate = (message) => {
    return (
      <div
        data-testid="pbi-empty-state-div"
        className={styles['powerbi-modal-warning-icon']}
      >
        <Icon
          name="alert"
          className="fill icon-yellow"
          width={24}
          data-testid="draft-icon"
        />
        <span>{message}</span>
      </div>
    );
  };

  const getErrorTemplate = () => {
    if (reportType === SELECT_REPORT_TYPE.TABLEAU) {
      return (
        <TableauErrorMessage
          title="Unable to fetch the report list"
          isShared={false}
        />
      );
    }
    return (
      <div className={styles['powerbi-modal-error']}>
        <Icon
          name="alert"
          className="fill icon-yellow"
          width={24}
          data-testid="draft-icon"
        />
        <span>
          Unable to fetch the report list. Please try again later. If the issue
          continues, please <SupportTicketLink /> for this issue
        </span>
      </div>
    );
  };

  const renderReportList = () => {
    let template;
    if (error) {
      template = getErrorTemplate();
    } else if (!error && isEmpty()) {
      template = getMessageTemplate(emptyListMessage);
    } else {
      template = getReportListTemplate();
    }

    return template;
  };

  return (
    <Modal
      backdrop={false}
      show={show}
      className={styles['powerbi-modal']}
      dialogClassName={
        isEmpty()
          ? styles['powerbi-modal-dialog--empty']
          : styles['powerbi-modal-dialog']
      }
      contentClassName={
        isEmpty()
          ? styles['powerbi-modal-content--empty']
          : styles['powerbi-modal-content']
      }
    >
      <Modal.Header className={styles['powerbi-modal-header']}>
        <Modal.Title className={styles['powerbi-modal-title']}>
          <PageTitle title={title} size="h4" />
          <p>{t('selectFile')}</p>
        </Modal.Title>
        <CloseButton onClick={onHide} />
      </Modal.Header>
      {isLoading && <Loader dataTestId="power-bi-reports-loader" />}
      {!isLoading && (
        <>
          <div className={styles['powerbi-modal-body-header']}>
            <div>
              <Icon
                name="folder-closed"
                width="22"
                className="icon outline icon-orange-theme"
              />
              <span>{project.name} workspace</span>
              <a
                className="external-source-link"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="project-workbench-link"
                href={workspaceUrl}
              >
                <Icon
                  name="box-arrow-up-right"
                  width="16"
                  className="icon fill icon-orange-theme"
                />
              </a>{' '}
            </div>
          </div>
          <div className={styles['powerbi-modal-body-inner']}>
            {renderReportList()}
          </div>
        </>
      )}
    </Modal>
  );
};
